import type { AnalyseDeLaFeuille, Regroupement } from '@nextilt/commun';
import type { BulleEnrichie, Feuille } from '../partage/api/types';
import { bullesConnues } from './contenuDesMarges';
import { barreDeLAvancement } from './lignesDuPanneauDAnalyse';

/*
 * **Ce que l'Analyse couchée montre des comptes du serveur** (chantier telephone-couche, 02 ;
 * maquette `app-au-telephone.html`, `analyse()`). Sans rendu, testé dans
 * `tests/client/projets/lignesDeLAnalyseCouchee.test.ts` : l'écran (`AnalyseCouchee.tsx`) lit `analyse` de
 * la feuille et les groupes du serveur, il ne compte rien.
 */

/** Une perle de l'avancement : faite, qui attend le monde, ou qui reste. */
export type Perle = 'faite' | 'monde' | 'reste';

/**
 * Les perles de l'avancement, une par bulle comptée : les faites d'abord, puis celles qui attendent
 * le monde, puis le reste, d'après la répartition du serveur. Au-delà des segments de la barre de
 * l'ordinateur (`barreDeLAvancement`), `null` : une seule barre, comme à l'ordinateur.
 */
export function perlesDeLAvancement(analyse: Pick<AnalyseDeLaFeuille, 'avancement' | 'repartition'>): Perle[] | null {
  if (barreDeLAvancement(analyse.avancement).sorte === 'barre') return null;
  const { faites, total } = analyse.avancement;
  const monde = analyse.repartition.find((r) => r.etat === 'monde')?.nombre ?? 0;
  const enMonde = Math.min(monde, total - faites);
  return Array.from({ length: total }, (_, i) => (i < faites ? 'faite' : i < faites + enMonde ? 'monde' : 'reste'));
}

/**
 * Les colonnes avec tout ouvert : chaque bulle qui contient quelque chose que la feuille connaît.
 * Le serveur range alors son dessous à part, dans les groupes.
 */
export function ouvertesDuCouche(feuille: Pick<Feuille, 'enfants' | 'carte'>): string[] {
  const connues = bullesConnues(feuille);
  const meres = new Set(connues.map((b) => b.parentId));
  return connues.filter((b) => b.nbEnfants > 0 && meres.has(b.id)).map((b) => b.id);
}

/** Dans une colonne, seules les bulles au bout sont des cartes. */
export function cartesDuGroupe<B extends Pick<BulleEnrichie, 'nbEnfants'>>(bulles: readonly B[]): B[] {
  return bulles.filter((b) => b.nbEnfants === 0);
}

/** La branche d'une carte : sa mère, quand ce n'est pas la feuille elle-même. */
export function brancheDeLaCarte(
  bulle: Pick<BulleEnrichie, 'parentId'>,
  feuille: Pick<Feuille, 'bulle' | 'enfants' | 'carte'>
): string | null {
  if (bulle.parentId === null || bulle.parentId === feuille.bulle.id) return null;
  return bullesConnues(feuille).find((b) => b.id === bulle.parentId)?.titre ?? null;
}

/** Le regroupement des colonnes couchées dure la visite ; par état au départ. */
let regroupementChoisi: Regroupement = 'etat';

export function regroupementDuCouche(): Regroupement {
  return regroupementChoisi;
}

export function choisirLeRegroupementDuCouche(par: Regroupement): void {
  regroupementChoisi = par;
}
